import { PublicTreeNotice } from "components";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type DragEvent, type FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { acceptedImageTypesAttribute, validateImageFile } from "utilities";

import { visibilityOptions } from "../data";

import type { NewTreeForm } from "../models";

import LineIcon from "./LineIcon";

import styles from "../HomePage.module.css";

type CreateTreeDialogProps = {
	/** Pro entitlement: when false the Private visibility option is locked behind a Pro mention. */
	canCreatePrivateTrees: boolean;
	coverPreview: string;
	errorMessage?: string;
	form: NewTreeForm;
	isSubmitting: boolean;
	onClose: () => void;
	/** Called with an already-validated image; the dialog rejects unsupported or oversized files itself. */
	onCoverSelect: (file: File) => void;
	onFormChange: (updates: Partial<NewTreeForm>) => void;
	onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	/** Optional overrides so the same dialog can be reused for editing an existing tree. */
	heading?: string;
	subheading?: string;
	submitLabel?: string;
	submittingLabel?: string;
	/**
	 * Opens the delete confirmation. Only supplied when editing a tree the caller owns, so the
	 * create flow and non-owners never render the action at all.
	 */
	onDelete?: () => void;
	deleteLabel?: string;
	/**
	 * Suppresses Escape-to-close while something is layered over this dialog (the delete
	 * confirmation). Without it the document-level handler would close this dialog out from under
	 * the confirmation — and could do so mid-request.
	 */
	isDismissBlocked?: boolean;
};

type VisibilityOption = (typeof visibilityOptions)[number];

const CreateTreeDialog = ({
	canCreatePrivateTrees,
	coverPreview,
	errorMessage,
	form,
	isSubmitting,
	onClose,
	onCoverSelect,
	onFormChange,
	onSubmit,
	heading,
	subheading,
	submitLabel,
	submittingLabel,
	onDelete,
	deleteLabel,
	isDismissBlocked
}: CreateTreeDialogProps) => {
	const { t } = useTranslation(["common", "homePage"]);
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const [coverError, setCoverError] = useState("");
	const [isCoverDragOver, setIsCoverDragOver] = useState(false);

	const closeOnEscape = useCallback(
		(event: KeyboardEvent): void => {
			if (event.key === "Escape" && !isDismissBlocked) {
				onClose();
			}
		},
		[isDismissBlocked, onClose]
	);

	useEffect(() => {
		document.addEventListener("keydown", closeOnEscape);

		return () => document.removeEventListener("keydown", closeOnEscape);
	}, [closeOnEscape]);

	const onTitleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			onFormChange({ title: event.target.value });
		},
		[onFormChange]
	);

	const onDescriptionChange = useCallback(
		(event: ChangeEvent<HTMLTextAreaElement>): void => {
			onFormChange({ description: event.target.value });
		},
		[onFormChange]
	);

	const onVisibilityClick = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>): void => {
			const { visibility } = event.currentTarget.dataset;
			if (visibility) {
				onFormChange({ visibility: visibility as NewTreeForm["visibility"] });
			}
		},
		[onFormChange]
	);

	const onCommentsToggle = useCallback((): void => {
		onFormChange({ commentsWhenPublic: !form.commentsWhenPublic });
	}, [form.commentsWhenPublic, onFormChange]);

	/**
	 * The single gate every cover file passes through, whichever way it arrived. `accept` on the input
	 * is advisory and drag and drop ignores it altogether, so the picker and the dropzone are held to
	 * the same rules and report the same message.
	 */
	const selectCoverFile = useCallback(
		(file: File | undefined): void => {
			if (!file) {
				return;
			}

			const rejection = validateImageFile(file);
			if (rejection) {
				setCoverError(t(`homePage:createDialog.coverError.${rejection}`));

				return;
			}

			setCoverError("");
			onCoverSelect(file);
		},
		[onCoverSelect, t]
	);

	const onCoverInputChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			selectCoverFile(event.target.files?.[0]);
			// Reset so picking the same file again still fires a change event — otherwise a file that was
			// rejected once could never be retried after the user shrank or converted it.
			event.target.value = "";
		},
		[selectCoverFile]
	);

	const onCoverDragOver = useCallback(
		(event: DragEvent<HTMLDivElement>): void => {
			// Dragging selected text or a link is not an upload: leave it to the dialog-wide guard below
			// so the panel neither lights up nor offers a drop it would silently ignore.
			if (isSubmitting || !event.dataTransfer.types.includes("Files")) {
				return;
			}

			// Both the preventDefault and stopPropagation matter: the first is what makes this element a
			// valid drop target at all, the second keeps the dialog-wide guard below from re-flagging the
			// drag as rejected and showing a "no drop" cursor over the one place a drop does work.
			event.preventDefault();
			event.stopPropagation();
			event.dataTransfer.dropEffect = "copy";
			setIsCoverDragOver(true);
		},
		[isSubmitting]
	);

	const onCoverDragLeave = useCallback((event: DragEvent<HTMLDivElement>): void => {
		// Crossing onto a child fires dragleave on the parent; ignore those or the panel flickers.
		if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
			return;
		}

		setIsCoverDragOver(false);
	}, []);

	const onCoverDrop = useCallback(
		(event: DragEvent<HTMLDivElement>): void => {
			event.preventDefault();
			event.stopPropagation();
			setIsCoverDragOver(false);

			if (isSubmitting) {
				return;
			}

			selectCoverFile(event.dataTransfer.files[0]);
		},
		[isSubmitting, selectCoverFile]
	);

	/**
	 * Anywhere that is not the cover panel refuses the drop outright. Without this the browser follows
	 * its default and navigates to the dropped file, throwing away everything typed into the dialog.
	 */
	const onDialogDragOver = useCallback((event: DragEvent<HTMLDivElement>): void => {
		event.preventDefault();
		event.dataTransfer.dropEffect = "none";
	}, []);

	const onDialogDrop = useCallback((event: DragEvent<HTMLDivElement>): void => {
		event.preventDefault();
	}, []);

	return (
		<div className={styles.modalOverlay} onDragOver={onDialogDragOver} onDrop={onDialogDrop}>
			<form
				ref={dialogRef}
				aria-labelledby="create-tree-title"
				aria-modal="true"
				className={styles.createDialog}
				onSubmit={onSubmit}
				role="dialog">
				<header className={styles.dialogHeader}>
					<span aria-hidden="true" className={styles.dialogLogoRing}>
						<img alt="" src={`${process.env.PUBLIC_URL}/assets/landing/main-logo.png`} />
					</span>
					<div className={styles.dialogHeading}>
						<h1 id="create-tree-title">{heading ?? t("homePage:createDialog.title")}</h1>
						<p>{subheading ?? t("homePage:createDialog.description")}</p>
					</div>
					<button
						aria-label={t("homePage:createDialog.close")}
						className={styles.dialogClose}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</header>

				<div className={styles.dialogBody}>
					<label className={styles.dialogField} htmlFor="create-tree-name">
						<span className={styles.fieldLabel}>
							{t("homePage:createDialog.titleLabel")} <span className={styles.requiredStar}>*</span>
						</span>
						<input
							className={styles.textInput}
							disabled={isSubmitting}
							id="create-tree-name"
							maxLength={fieldLimits.treeTitle}
							onChange={onTitleChange}
							type="text"
							value={form.title}
							required
						/>
					</label>

					<label className={styles.dialogField} htmlFor="create-tree-description">
						<span className={styles.fieldLabel}>
							{t("homePage:createDialog.descriptionLabel")}{" "}
							<span className={styles.optionalText}>({t("homePage:createDialog.optional")})</span>
						</span>
						<textarea
							className={styles.textArea}
							disabled={isSubmitting}
							id="create-tree-description"
							maxLength={fieldLimits.treeDescription}
							onChange={onDescriptionChange}
							rows={4}
							value={form.description}
						/>
					</label>

					<div className={styles.dialogSplit}>
						<div className={styles.coverGroup}>
							<span className={styles.fieldLabel}>
								{t("homePage:createDialog.coverImage")}{" "}
								<span className={styles.optionalText}>({t("homePage:createDialog.optional")})</span>
							</span>
							<div
								className={`${styles.coverDropzone} ${
									isCoverDragOver ? styles.coverDropzoneActive : ""
								}`}
								onDragLeave={onCoverDragLeave}
								onDragOver={onCoverDragOver}
								onDrop={onCoverDrop}>
								<img alt="" className={styles.coverPreview} src={coverPreview} />
								<div className={styles.uploadDetails}>
									<LineIcon name="upload" />
									<span className={styles.uploadTitle}>{t("homePage:createDialog.uploadTitle")}</span>
									<span className={styles.uploadMeta}>{t("homePage:createDialog.uploadMeta")}</span>
									<input
										accept={acceptedImageTypesAttribute}
										className={styles.coverInput}
										disabled={isSubmitting}
										id="create-tree-cover"
										onChange={onCoverInputChange}
										type="file"
									/>
									<label className={styles.uploadButton} htmlFor="create-tree-cover">
										<LineIcon name="upload" />
										<span>{t("homePage:createDialog.uploadCover")}</span>
									</label>
									<span className={styles.uploadMeta}>{t("homePage:createDialog.dropHint")}</span>
								</div>
							</div>
							{coverError && (
								<span className={styles.coverError} role="alert">
									{coverError}
								</span>
							)}
						</div>

						<div className={styles.visibilityGroup}>
							<span className={styles.fieldLabel}>
								{t("homePage:createDialog.visibility")} <span className={styles.requiredStar}>*</span>
							</span>
							<div
								aria-label={t("homePage:createDialog.visibilityLabel")}
								className={styles.visibilityControl}
								role="radiogroup">
								{visibilityOptions.map((option: VisibilityOption) => {
									const isProLocked = option.value === "Private" && !canCreatePrivateTrees;

									return (
										<button
											key={option.value}
											aria-checked={form.visibility === option.value}
											className={`${styles.visibilityOption} ${
												form.visibility === option.value ? styles.visibilityOptionSelected : ""
											} ${isProLocked ? styles.visibilityOptionLocked : ""}`}
											data-visibility={option.value}
											disabled={isProLocked}
											onClick={onVisibilityClick}
											role="radio"
											title={isProLocked ? t("homePage:createDialog.privateNeedsPro") : undefined}
											type="button">
											<LineIcon name={option.icon} />
											<span>
												{t(`homePage:createDialog.visibilityOptions.${option.value}.label`)}
											</span>
											{isProLocked && (
												<span className={styles.proTag}>
													{t("homePage:createDialog.proTag")}
												</span>
											)}
										</button>
									);
								})}
							</div>
							<div className={styles.visibilityCaptions}>
								{visibilityOptions.map((option: VisibilityOption) => (
									<span key={option.value}>
										{option.value === "Private" && !canCreatePrivateTrees
											? t("homePage:createDialog.privateNeedsPro")
											: t(`homePage:createDialog.visibilityOptions.${option.value}.caption`)}
									</span>
								))}
							</div>
							{/* Before anything is published: a public tree shows the living people in it to anyone. */}
							{form.visibility === "Public" && <PublicTreeNotice />}
						</div>
					</div>

					<div className={styles.commentsRow}>
						<span>
							{t("homePage:createDialog.enableComments")}{" "}
							<span className={styles.optionalText}>({t("homePage:createDialog.optional")})</span>
						</span>
						<button
							aria-checked={form.commentsWhenPublic}
							aria-label={t("homePage:createDialog.enableComments")}
							className={`${styles.switchControl} ${
								form.commentsWhenPublic ? styles.switchControlOn : ""
							}`}
							disabled={isSubmitting}
							onClick={onCommentsToggle}
							role="switch"
							type="button">
							<span />
						</button>
					</div>
					{errorMessage && <div className={styles.verifyMessage}>{errorMessage}</div>}
				</div>

				<footer className={styles.dialogFooter}>
					{onDelete && (
						<button
							className={styles.deleteButton}
							disabled={isSubmitting}
							onClick={onDelete}
							type="button">
							<LineIcon name="trash" />
							<span>{deleteLabel}</span>
						</button>
					)}
					<button className={styles.cancelButton} onClick={onClose} type="button">
						{t("common:cancel")}
					</button>
					<button className={styles.createButton} disabled={isSubmitting} type="submit">
						<LineIcon name="sparkle" />
						<span>
							{isSubmitting
								? submittingLabel ?? t("homePage:createDialog.creating")
								: submitLabel ?? t("homePage:createDialog.create")}
						</span>
					</button>
				</footer>
			</form>
		</div>
	);
};

export default CreateTreeDialog;
